import { useNavigate } from 'react-router-dom'

import { ActionItem, Box, Icon, Text } from '@factorialco/deprecated-design-system'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMemo } from 'modules/core/hooks/useMemo'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import filesStore from 'modules/documents/components/Documents/store'
import GridList from 'modules/documents/components/GridList'
import { Folder } from 'modules/documents/hooks/useGetFolders'
import { setSubfoldersCountForSkeletons } from 'modules/documents/lib/Documents'
import {
  getIcon,
  getTitle,
  isInternalCompany,
  isPublicCompany,
} from 'modules/documents/lib/Folders'

interface Props {
  employeeId?: string
  parentFolderId?: string
  folder: Folder
  readonly: boolean
  onFolderSelected: (folder: Folder) => void
}

const FolderItem = ({ employeeId, parentFolderId, folder, readonly, onFolderSelected }: Props) => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const { employee: currentEmployee } = useCurrentSession()
  const isCurrentEmployee = employeeId === currentEmployee?.id

  const newDocumentFolderUrl = useMemo(() => {
    if (isPublicCompany(folder)) {
      if (parentFolderId) {
        return navigation.files.companyFiles.folderId.new.fullPath({ folderId: parentFolderId })
      } else {
        return navigation.files.companyFiles.list.new.fullPath()
      }
    } else if (isInternalCompany(folder)) {
      if (parentFolderId) {
        return navigation.files.managementDocuments.folderId.new.fullPath({
          folderId: parentFolderId,
        })
      } else {
        return navigation.files.managementDocuments.list.new.fullPath()
      }
    } else if (isCurrentEmployee) {
      if (parentFolderId) {
        return navigation.documents.folderId.new.fullPath({ folderId: parentFolderId })
      } else {
        return navigation.documents.list.new.fullPath()
      }
    } else {
      if (parentFolderId) {
        return navigation.employees.id.documents.folderId.new.fullPath({
          id: String(employeeId),
          folderId: parentFolderId,
        })
      } else {
        return navigation.employees.id.documents.list.new.fullPath({
          id: String(employeeId),
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId, isCurrentEmployee, parentFolderId])

  const navigateUrl = useMemo(() => {
    if (isPublicCompany(folder)) {
      return navigation.files.companyFiles.folderId.fullPath({ folderId: folder.id })
    } else if (isInternalCompany(folder)) {
      return navigation.files.managementDocuments.folderId.fullPath({
        folderId: folder.id,
      })
    } else if (currentEmployee && currentEmployee.id === employeeId) {
      return navigation.documents.folderId.fullPath({ folderId: folder.id })
    } else if (employeeId) {
      return navigation.employees.id.documents.folderId.fullPath({
        id: String(employeeId),
        folderId: folder.id,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentEmployee?.id, employeeId, folder.id])

  const onDrop = (files: File[]) => {
    if (readonly) return null

    filesStore.setFiles(files)

    onFolderSelected(folder)

    navigate(newDocumentFolderUrl)
  }

  const getCountersText = (documentsCounter: number, subFoldersCounter: number) => {
    const documentsText =
      documentsCounter > 0 ? i18n.tp('documents.files', { count: documentsCounter }) : ''
    const subfoldersText =
      subFoldersCounter > 0 ? i18n.tp('documents.folders', { count: subFoldersCounter }) : ''

    if (documentsText && subfoldersText) {
      return `${subfoldersText} / ${documentsText}`
    }

    return documentsText || subfoldersText
  }

  const saveSubfolderCount = () => {
    setSubfoldersCountForSkeletons(folder.counterSubfolders, folder.id)
  }

  return (
    <GridList.ListItem disableClick onDrop={onDrop} key={folder.id}>
      <ActionItem to={navigateUrl} slim onClick={saveSubfolderCount} showIcon={false}>
        <Box
          gap="s16"
          flexDirection="row"
          justifyContent="spaceBetween"
          flexGrow
          minHeight="s48"
          width="full"
          style={{ marginLeft: '-4px' }} // To fix a padding added by the ActionItem by default
        >
          <Box
            flexDirection="row"
            alignItems="center"
            gap="s12"
            style={{ wordWrap: 'break-word', width: '80%' }}
          >
            <Box>
              <Icon.Large icon={getIcon(folder.name)} />
            </Box>
            <Box width="full">
              <Text ellipsis size="300" color="grey1000" weight="medium">
                {getTitle(folder.name)}
              </Text>
              <Box>
                <Text size="200" color="grey800" weight="medium">
                  {getCountersText(folder.counterDocuments, folder.counterSubfolders)}
                </Text>
              </Box>
            </Box>
          </Box>
          <Box width="s16">
            <Icon.Medium icon="arrow-right" />
          </Box>
        </Box>
      </ActionItem>
    </GridList.ListItem>
  )
}

export default FolderItem
